import { useMemo } from 'react';
import { Grid } from 'antd';

import AccountOverview from './AccountOverview';
import CreditOverview from './CreditOverview';
import DebitOverview from './DebitOverview';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SimpleGrid from 'components/SimpleGrid/SimpleGrid';
import { PRODUCT } from 'utilities/constants';
import useAccountsInfo from 'hooks/useAccountsInfo';

const PRODUCT_ORDER = {
  [PRODUCT.CREDIT_CARD]: 1,
  [PRODUCT.DEBIT]: 2,
};

const AccountListSkeleton = ({ children, loading }) => {
  const breakpoints = Grid.useBreakpoint();
  const count = breakpoints.xxl ? 3 : breakpoints.lg ? 2 : 1;
  const span = breakpoints.xxl ? '4' : breakpoints.lg ? '6' : '12';
  const accounts = [];
  for (let index = 0; index < count; index++) {
    accounts.push(
      <div
        style={{ gridColumn: `span ${span}` }}
        key={`account-placeholder-${index}`}
      >
        <AccountOverview query={{ called: false, loading: true }}>
          <span />
        </AccountOverview>
      </div>,
    );
  }
  return loading ? <SimpleGrid>{accounts}</SimpleGrid> : children;
};

const CustomerOverviewPage = () => {
  const breakpoints = Grid.useBreakpoint();
  const { accountsQuery, accounts, simpleAccounts } = useAccountsInfo();

  const sortedSimpleAccounts = useMemo(() => {
    const toSort = simpleAccounts || [];
    return [...toSort].sort((account1, account2) => {
      return (
        (PRODUCT_ORDER[account1.product.name] || Infinity) -
        (PRODUCT_ORDER[account2.product.name] || Infinity)
      );
    });
  }, [simpleAccounts]);

  const renderAccountOverview = (simpleAccount) => {
    let OverviewComponent;
    let params = {
      accountId: simpleAccount.id,
      simpleAccount,
    };
    switch (simpleAccount.product.name) {
      case PRODUCT.CREDIT_CARD:
        OverviewComponent = CreditOverview;
        params.account = accounts.find(
          (accounts) => accounts.id === simpleAccount.id,
        );
        break;
      case PRODUCT.DEBIT:
        OverviewComponent = DebitOverview;
        break;
      default:
        OverviewComponent = AccountOverview;
        params.accountTitle = simpleAccount.product.name;
    }

    const span = breakpoints.xxl ? '4' : breakpoints.lg ? '6' : '12';

    return (
      <div
        style={{ gridColumn: `span ${span}` }}
        key={`account-overview-${simpleAccount.id}`}
      >
        <OverviewComponent {...params}></OverviewComponent>
      </div>
    );
  };

  return (
    <QueryBoundary
      errorMessage="Failed to load Account Overviews."
      query={accountsQuery}
      skeleton={AccountListSkeleton}
    >
      <SimpleGrid style={{ marginBottom: 'var(--spacing-xs)' }}>
        {sortedSimpleAccounts.map(renderAccountOverview)}
      </SimpleGrid>
    </QueryBoundary>
  );
};

export default CustomerOverviewPage;
